import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { GitBranch, Plus, Settings, Sparkles, ChevronRight, X, Tag, ZoomIn, ZoomOut, RotateCcw, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSupportSettingsStore, ExtendedWorkflow, WorkflowStep, WorkflowStepAction, saveJson, syncSettingFromServer } from '../../../store/supportSettingsStore';
import { useWorkflowStore, WorkflowTransition } from '../../../store/workflowStore';
import { cn } from '../../../lib/utils';
import { Dialog, DialogContent, DialogTitle } from '../../../components/Dialog';

export type { WorkflowStep, WorkflowStepAction, ExtendedWorkflow };

export const TICKET_TYPES_LIST = [
  { id: 'Incident', label: 'Incident', code: 'WF-INC-01', desc: 'Unplanned IT service outage or degraded performance' },
  { id: 'Service Request', label: 'Service Request', code: 'WF-SR-01', desc: 'User requests for access, hardware procurement, or provisioning' },
  { id: 'Problem', label: 'Problem', code: 'WF-PR-01', desc: 'Recurring incident root cause analysis & known error database' },
  { id: 'Change', label: 'Change Request', code: 'WF-CH-01', desc: 'CAB approval, change scheduling, and deployment verification' },
  { id: 'Bug Report', label: 'Bug Report', code: 'WF-BUG-01', desc: 'Software defects, repro verification, and engineering patches' },
  { id: 'Feature Request', label: 'Feature Request', code: 'WF-FEAT-01', desc: 'Product enhancement suggestions and roadmap qualification' },
  { id: 'Inquiry', label: 'Question / Inquiry', code: 'WF-INQ-01', desc: 'General customer inquiries and knowledge base assistance' },
];

const NODE_WIDTH = 195;
const NODE_HEIGHT = 56;

// Default node positions matching the layout in image
const DEFAULT_NODE_POSITIONS: Record<string, { x: number; y: number }> = {
  'New': { x: 30, y: 45 },
  'Assigned': { x: 255, y: 45 },
  'In Progress': { x: 480, y: 45 },
  'Resolved': { x: 705, y: 45 },
  'Closed': { x: 930, y: 45 },
  'Pending Customer': { x: 140, y: 220 },
  'Pending Vendor': { x: 390, y: 220 },
  'Escalated': { x: 640, y: 220 },
};

function WorkflowDrawer({ children, title, onClose, wide = false }: {
  children: React.ReactNode;
  title: string;
  onClose: () => void;
  wide?: boolean;
}) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent side="right" hideClose aria-describedby={undefined} className={cn("gap-0 overflow-hidden p-0", wide ? "w-full sm:max-w-2xl lg:max-w-3xl" : "w-full sm:max-w-lg")}>
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export function WorkflowsView() {
  const {
    extendedWorkflows: storeWorkflows,
    fetchExtendedWorkflows,
    saveExtendedWorkflow,
    deleteWorkflowStage,
    ticketTypes: storeTicketTypes,
    fetchTicketTypes,
    ticketActions: storeActions,
    fetchTicketActions,
  } = useSupportSettingsStore();

  const {
    workflows: simpleWorkflows,
    fetchWorkflows: fetchSimpleWorkflows,
    updateWorkflow: updateSimpleWorkflow,
  } = useWorkflowStore();

  const ticketTypesList = useMemo(() => {
    if (storeTicketTypes && storeTicketTypes.length > 0) {
      return storeTicketTypes.map((t) => {
        const codeSuffix = (t.code || t.tagPrefix || t.prefix || '').trim();
        return {
          id: t.id,
          name: t.name,
          code: codeSuffix || `WF-${t.name.toUpperCase().slice(0, 3)}-01`,
          teamName: t.teamName || 'Support Team',
          tagPrefix: t.tagPrefix || t.prefix || 'INC',
          label: codeSuffix ? `${t.name} (${codeSuffix})` : t.name,
          desc: t.description || `${t.name} lifecycle workflow`,
        };
      });
    }
    return TICKET_TYPES_LIST.map((t) => ({
      id: t.id,
      name: t.label,
      code: t.code,
      teamName: 'Support Team',
      tagPrefix: 'INC',
      label: t.label,
      desc: t.desc,
    }));
  }, [storeTicketTypes]);

  const [workflows, setWorkflows] = useState<ExtendedWorkflow[]>(storeWorkflows || []);
  const [selectedTicketTypeId, setSelectedTicketTypeId] = useState<string>('');
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [showWorkflowSettingsModal, setShowWorkflowSettingsModal] = useState<boolean>(false);
  const [showCreateWorkflowModal, setShowCreateWorkflowModal] = useState<boolean>(false);

  useEffect(() => {
    if (ticketTypesList.length > 0) {
      if (!selectedTicketTypeId || !ticketTypesList.some((t) => t.id === selectedTicketTypeId)) {
        setSelectedTicketTypeId(ticketTypesList[0].id);
      }
    }
  }, [ticketTypesList, selectedTicketTypeId]);

  const selectedTypeObj = useMemo(() => {
    return ticketTypesList.find((t) => t.id === selectedTicketTypeId) || ticketTypesList[0] || null;
  }, [ticketTypesList, selectedTicketTypeId]);

  const selectedTicketType = selectedTypeObj?.name || 'Incident';

  const [workflowSettingsForm, setWorkflowSettingsForm] = useState<{
    name: string;
    code: string;
    description: string;
    allowedTicketTypes: string[];
    isDefault: boolean;
  }>({
    name: '',
    code: '',
    description: '',
    allowedTicketTypes: [],
    isDefault: false,
  });

  const [createWorkflowForm, setCreateWorkflowForm] = useState<{
    name: string;
    code: string;
    description: string;
    allowedTicketTypes: string[];
    cloneCurrent: boolean;
    isDefault: boolean;
  }>({
    name: '',
    code: '',
    description: '',
    allowedTicketTypes: ['Incident'],
    cloneCurrent: true,
    isDefault: false,
  });

  const [isDrawingMode, setIsDrawingMode] = useState<boolean>(false);
  const [canvasZoom, setCanvasZoom] = useState<number>(1);

  // Dragging state
  const [nodePositions, setNodePositions] = useState<Record<string, { x: number; y: number }>>({});

  const [draggingNode, setDraggingNode] = useState<{ id: string; startMouseX: number; startMouseY: number; startNodeX: number; startNodeY: number } | null>(null);

  // Wire drawing state
  const [activeWiring, setActiveWiring] = useState<{
    sourceStepId: string;
    sourceStepName: string;
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
  } | null>(null);

  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);

  // Modals state
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [showAddActionModal, setShowAddActionModal] = useState(false);
  const [showEditActionModal, setShowEditActionModal] = useState(false);
  const [targetStatusForNewAction, setTargetStatusForNewAction] = useState<string>('New');
  const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null);
  const [editingAction, setEditingAction] = useState<{ stepId: string; action: WorkflowStepAction } | null>(null);

  // New Step Form
  const [newStepForm, setNewStepForm] = useState<{
    name: string;
    type: WorkflowStep['type'];
    description: string;
    slaTimeoutHours: number;
  }>({
    name: '',
    type: 'in_progress',
    description: '',
    slaTimeoutHours: 8,
  });

  // Action Form
  const [actionForm, setActionForm] = useState<{
    actionButtonId?: string;
    actionCode?: string;
    name: string;
    buttonName: string;
    targetStepName: string;
    buttonStyle: 'primary' | 'secondary' | 'warning' | 'success' | 'destructive' | 'outline';
    lineStyle: 'solid' | 'dashed';
    requiresNote: boolean;
    requiresApproval: boolean;
    autoTrigger: 'none' | 'customer_reply' | 'after_days';
    autoAfterDays: number;
  }>({
    actionButtonId: '',
    actionCode: '',
    name: '',
    buttonName: '',
    targetStepName: 'In Progress',
    buttonStyle: 'secondary',
    lineStyle: 'solid',
    requiresNote: false,
    requiresApproval: false,
    autoTrigger: 'none',
    autoAfterDays: 3,
  });

  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void fetchExtendedWorkflows();
    void fetchTicketTypes();
    void fetchSimpleWorkflows();
    void fetchTicketActions();
  }, [fetchExtendedWorkflows, fetchTicketTypes, fetchSimpleWorkflows, fetchTicketActions]);

  useEffect(() => {
    if (storeWorkflows && storeWorkflows.length > 0) {
      setWorkflows(storeWorkflows);
    }
  }, [storeWorkflows]);

  // Load positions when switching ticket type
  useEffect(() => {
    if (selectedTypeObj) {
      void syncSettingFromServer(`teamone_wf_positions_${selectedTypeObj.id}`, {}).then((pos) => {
        setNodePositions(pos || {});
      });
    }
  }, [selectedTypeObj]);

  // Multiple workflows matching the selected ticket type
  const workflowsForSelectedType = useMemo(() => {
    if (!selectedTypeObj) return workflows;
    return workflows.filter((w) => {
      // 1. Direct ticketTypeId match
      if (w.ticketTypeId && w.ticketTypeId === selectedTypeObj.id) return true;
      // 2. Workflow code match (e.g. WF_SD_FAULT or WF_CHAN_CAB)
      if (selectedTypeObj.code && (w.code === `WF_${selectedTypeObj.code}` || w.code.includes(selectedTypeObj.code))) return true;
      // 3. Ticket type name match
      if (w.ticketType && selectedTypeObj.name && w.ticketType.toLowerCase() === selectedTypeObj.name.toLowerCase()) return true;
      // 4. Allowed ticket types match
      if (Array.isArray(w.allowedTicketTypes) && selectedTypeObj.name && w.allowedTicketTypes.some(
        (att) => att.toLowerCase() === selectedTypeObj.name.toLowerCase()
      )) return true;
      return false;
    });
  }, [workflows, selectedTypeObj]);

  // Current workflow for selected ticket type (or selected workflow id)
  const currentWf: ExtendedWorkflow = useMemo(() => {
    if (selectedWorkflowId) {
      const match = workflows.find((w) => w.id === selectedWorkflowId);
      if (match) return match;
    }
    if (workflowsForSelectedType.length > 0) {
      const defaultWf = workflowsForSelectedType.find((w) => w.isDefault) || workflowsForSelectedType[0];
      return defaultWf;
    }
    if (workflows.length > 0) {
      return workflows[0];
    }
    return {
      id: `wf-${selectedTypeObj?.id || 'default'}`,
      name: `${selectedTypeObj?.name || 'Standard'} Lifecycle Workflow`,
      code: `WF_${selectedTypeObj?.code || 'GEN'}`,
      ticketType: selectedTypeObj?.name || 'Incident',
      allowedTicketTypes: [selectedTypeObj?.name || 'Incident'],
      description: `${selectedTypeObj?.name || 'Ticket'} lifecycle workflow`,
      isDefault: true,
      isActive: true,
      workingHoursSchedule: 'Mon-Fri 09:00 - 18:00 IST',
      allowedTeams: ['Support Team'],
      steps: [],
    };
  }, [selectedWorkflowId, workflows, workflowsForSelectedType, selectedTypeObj]);

  // Synchronize with stores & storage
  const saveWorkflowUpdates = useCallback((updatedWf: ExtendedWorkflow) => {
    const nextList = workflows.some((w) => w.id === updatedWf.id)
      ? workflows.map((w) => (w.id === updatedWf.id ? updatedWf : w))
      : [...workflows, updatedWf];

    setWorkflows(nextList);
    localStorage.setItem('teamone_extended_workflows', JSON.stringify(nextList));
    void saveExtendedWorkflow(updatedWf);

    // Sync with simple workflowStore
    const transitions: WorkflowTransition[] = [];
    updatedWf.steps.forEach((step) => {
      step.actions.forEach((act) => {
        transitions.push({
          id: act.id,
          label: act.buttonName,
          fromState: step.name,
          toState: act.targetStepName || (act.targetStepId === 'remain' ? step.name : 'In Progress'),
          buttonStyle: act.buttonStyle,
          requiresNote: act.requiresNote,
        });
      });
    });

    const simpleMatch = simpleWorkflows.find((w) => w.type.toLowerCase() === updatedWf.ticketType.toLowerCase());
    if (simpleMatch) {
      void updateSimpleWorkflow(simpleMatch.id, {
        states: updatedWf.steps.map((s) => s.name),
        transitions,
      });
    }

    toast.success(`Workflow & actions for "${updatedWf.ticketType}" saved!`);
  }, [workflows, saveExtendedWorkflow, simpleWorkflows, updateSimpleWorkflow]);

  // Node position helper
  const getNodePos = (stepName: string, idx: number = 0) => {
    if (nodePositions[stepName]) return nodePositions[stepName];
    if (DEFAULT_NODE_POSITIONS[stepName] && idx === 0 && DEFAULT_NODE_POSITIONS[stepName].x === 40) {
      // Use standard positions if available
      return DEFAULT_NODE_POSITIONS[stepName];
    }
    // Dynamic clean fallbacks: 4 columns, cleanly spaced
    const cols = 4;
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    return { x: 30 + col * 235, y: 45 + row * 180 };
  };

  // Node Styling Helper for visualizer canvas
  const getNodePillStyle = (stepName: string) => {
    const norm = stepName.toLowerCase();
    if (norm.includes('new') || norm.includes('triage')) {
      return 'border-l-4 border-l-slate-400 dark:border-l-slate-500';
    }
    if (norm.includes('progress') || norm.includes('investig') || norm.includes('assign')) {
      return 'border-l-4 border-l-primary';
    }
    if (norm.includes('resolved') || norm.includes('fulfill')) {
      return 'border-l-4 border-l-success';
    }
    if (norm.includes('closed') || norm.includes('archiv')) {
      return 'border-l-4 border-l-emerald-700 dark:border-l-emerald-600';
    }
    if (norm.includes('customer') || norm.includes('user') || norm.includes('awaiting')) {
      return 'border-l-4 border-l-warning';
    }
    if (norm.includes('vendor') || norm.includes('supplier') || norm.includes('external')) {
      return 'border-l-4 border-l-sky-500';
    }
    if (norm.includes('escalat')) {
      return 'border-l-4 border-l-destructive';
    }
    return 'border-l-4 border-l-primary/60';
  };

  const getStatusBadgeClass = (stepName: string) => {
    const norm = stepName.toLowerCase();
    if (norm.includes('new') || norm.includes('triage')) {
      return 'bg-secondary text-foreground border border-border';
    }
    if (norm.includes('progress') || norm.includes('investig') || norm.includes('assign')) {
      return 'bg-primary/10 text-primary border border-primary/20';
    }
    if (norm.includes('resolved') || norm.includes('fulfill') || norm.includes('closed')) {
      return 'bg-success/10 text-success-strong border border-success/20';
    }
    if (norm.includes('customer') || norm.includes('user') || norm.includes('awaiting')) {
      return 'bg-warning/10 text-warning-strong border border-warning/20';
    }
    if (norm.includes('escalat')) {
      return 'bg-destructive/10 text-destructive border border-destructive/20';
    }
    return 'bg-secondary text-foreground border border-border';
  };

  // Action Button Styling Helper matching Image 2
  const getActionButtonStyle = (buttonName: string, buttonStyle?: string) => {
    const norm = buttonName.toLowerCase();
    if (buttonStyle === 'primary' || norm === 'assign' || norm === 'investigate escalation') {
      return 'bg-[#2563eb] text-white hover:bg-blue-700 shadow-2xs font-semibold';
    }
    if (norm === 'escalate' || buttonStyle === 'destructive') {
      return 'bg-[#ffe4e6] text-[#be123c] border border-[#fecdd3] hover:bg-rose-100 font-semibold';
    }
    if (norm === 'resolve' || buttonStyle === 'success') {
      return 'bg-[#dcfce7] text-[#15803d] border border-[#bbf7d0] hover:bg-emerald-100 font-semibold';
    }
    if (norm === 'cancel') {
      return 'bg-card text-foreground border border-border hover:bg-muted font-medium';
    }
    if (norm === 'add note' || norm === 'put on hold' || norm === 'close' || norm === 'view history' || buttonStyle === 'outline') {
      return 'bg-[#f1f5f9] dark:bg-slate-800 text-[#475569] dark:text-slate-300 border border-[#e2e8f0] dark:border-slate-700 hover:bg-slate-200 font-medium';
    }
    return 'bg-[#dbeafe] text-[#1d4ed8] border border-[#bfdbfe] hover:bg-blue-100 font-semibold';
  };

  // ═════════════════════════════════════════════════════════════════
  // DRAGGING AND DRAWING HANDLERS
  // ═════════════════════════════════════════════════════════════════
  const handleNodeMouseDown = (e: React.MouseEvent, stepName: string) => {
    if (isDrawingMode) return;
    e.stopPropagation();
    const pos = getNodePos(stepName);
    setDraggingNode({
      id: stepName,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startNodeX: pos.x,
      startNodeY: pos.y,
    });
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (draggingNode) {
      const dx = (e.clientX - draggingNode.startMouseX) / canvasZoom;
      const dy = (e.clientY - draggingNode.startMouseY) / canvasZoom;
      const nextX = Math.max(10, Math.round(draggingNode.startNodeX + dx));
      const nextY = Math.max(10, Math.round(draggingNode.startNodeY + dy));

      setNodePositions((prev) => ({
        ...prev,
        [draggingNode.id]: { x: nextX, y: nextY },
      }));
    } else if (activeWiring && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setActiveWiring((prev) =>
        prev
          ? {
              ...prev,
              currentX: (e.clientX - rect.left) / canvasZoom,
              currentY: (e.clientY - rect.top) / canvasZoom,
            }
          : null
      );
    }
  };

  const handleCanvasMouseUp = () => {
    if (draggingNode) {
      saveJson(`teamone_wf_positions_${selectedTicketType}`, nodePositions);
      setDraggingNode(null);
    }
    if (activeWiring) {
      setActiveWiring(null);
    }
  };

  // Start wiring from port
  const handlePortMouseDown = (e: React.MouseEvent, step: WorkflowStep, portSide: 'top' | 'bottom' | 'left' | 'right') => {
    e.stopPropagation();
    if (!canvasRef.current) return;
    const pos = getNodePos(step.name);
    const nodeW = NODE_WIDTH;
    const nodeH = NODE_HEIGHT;

    let pX = pos.x + nodeW / 2;
    let pY = pos.y + nodeH / 2;
    if (portSide === 'left') pX = pos.x;
    if (portSide === 'right') pX = pos.x + nodeW;
    if (portSide === 'top') pY = pos.y;
    if (portSide === 'bottom') pY = pos.y + nodeH;

    setActiveWiring({
      sourceStepId: step.id,
      sourceStepName: step.name,
      startX: pX,
      startY: pY,
      currentX: pX,
      currentY: pY,
    });
  };

  // Drop wire on target node
  const handleNodeDropWire = (targetStep: WorkflowStep) => {
    if (!activeWiring) return;
    if (activeWiring.sourceStepName === targetStep.name) {
      toast('Cannot wire stage to itself');
      setActiveWiring(null);
      return;
    }

    const sourceStep = currentWf.steps.find((s) => s.id === activeWiring.sourceStepId);
    if (!sourceStep) {
      setActiveWiring(null);
      return;
    }

    const actionLabel = window.prompt(
      `Enter Action Button Name for flow "${sourceStep.name}" → "${targetStep.name}":`,
      `Transition to ${targetStep.name}`
    );

    if (!actionLabel) {
      setActiveWiring(null);
      return;
    }

    const newAct: WorkflowStepAction = {
      id: `act-${Date.now()}`,
      name: actionLabel,
      buttonName: actionLabel,
      actionType: 'agent_action',
      targetStepId: targetStep.id,
      targetStepName: targetStep.name,
      buttonStyle: targetStep.name.toLowerCase().includes('resolv') ? 'success' : targetStep.name.toLowerCase().includes('escalat') ? 'destructive' : 'secondary',
    };

    const nextSteps = currentWf.steps.map((s) => {
      if (s.id === sourceStep.id) {
        return {
          ...s,
          actions: [...s.actions, newAct],
        };
      }
      return s;
    });

    saveWorkflowUpdates({ ...currentWf, steps: nextSteps });
    setActiveWiring(null);
    toast.success(`Wired "${sourceStep.name}" → "${targetStep.name}" with action "${actionLabel}"`);
  };

  // ═════════════════════════════════════════════════════════════════
  // CALCULATE SVG FLOW CONNECTIONS (EXACT LINES MATCHING IMAGE 1)
  // ═════════════════════════════════════════════════════════════════
  const renderedConnections = useMemo(() => {
    if (!currentWf || !Array.isArray(currentWf.steps)) return [];
    const list: Array<{
      id: string;
      sourceStepName: string;
      targetStepName: string;
      actionName: string;
      actionId: string;
      sourceStepId: string;
      isDashed: boolean;
      pathD: string;
      arrowColor: string;
      labelX: number;
      labelY: number;
    }> = [];

    const nodeW = NODE_WIDTH;
    const nodeH = NODE_HEIGHT;

    currentWf.steps.forEach((step, sIdx) => {
      const srcPos = getNodePos(step.name, sIdx);
      const isSrcBranch = step.name.toLowerCase().includes('pending') || step.name.toLowerCase().includes('escalat');

      step.actions.forEach((act) => {
        if (!act.targetStepName || act.targetStepName === 'remain' || act.targetStepName === step.name) return;

        const targetStep = currentWf.steps.find((ts) => ts.name === act.targetStepName);
        if (!targetStep) return;

        const tIdx = currentWf.steps.indexOf(targetStep);
        const tgtPos = getNodePos(targetStep.name, tIdx >= 0 ? tIdx : 0);
        const isTgtBranch = targetStep.name.toLowerCase().includes('pending') || targetStep.name.toLowerCase().includes('escalat');

        const isDashed = act.lineStyle === 'dashed' || (act.lineStyle !== 'solid' && (isSrcBranch || isTgtBranch || act.buttonStyle === 'warning' || act.buttonStyle === 'destructive' || act.buttonName.toLowerCase().includes('hold')));
        const arrowColor = isDashed ? '#64748b' : '#334155';

        // Calculate connection geometry
        let pathD = '';
        let labelX = 0;
        let labelY = 0;

        if (!isSrcBranch && !isTgtBranch && Math.abs(srcPos.y - tgtPos.y) < 60) {
          // Direct horizontal sequential flow (New -> Assigned -> In Progress -> Resolved -> Closed)
          const startX = srcPos.x + nodeW;
          const startY = srcPos.y + nodeH / 2;
          const endX = tgtPos.x;
          const endY = tgtPos.y + nodeH / 2;

          pathD = `M ${startX} ${startY} L ${endX} ${endY}`;
          labelX = (startX + endX) / 2;
          labelY = (startY + endY) / 2 - 10;
        } else if (!isSrcBranch && isTgtBranch) {
          // Main pipeline -> Branch below (e.g. New/Assigned -> Pending Customer)
          // Exact shape from Image: leaves left side of main, goes down and right into Pending Customer
          const startX = srcPos.x + 20;
          const startY = srcPos.y + nodeH;
          const endX = tgtPos.x;
          const endY = tgtPos.y + nodeH / 2;

          const cornerX = Math.min(srcPos.x - 30, tgtPos.x - 30);
          pathD = `M ${startX} ${startY} L ${cornerX} ${startY + 20} L ${cornerX} ${endY} L ${endX} ${endY}`;
          labelX = cornerX + 15;
          labelY = (startY + endY) / 2;
        } else if (isSrcBranch && !isTgtBranch) {
          // Branch -> Main pipeline above (e.g. Pending Customer -> In Progress, Pending Vendor -> In Progress, Escalated -> Resolved)
          // Exact shape from Image: goes up from top of card into bottom of In Progress / Resolved
          const startX = srcPos.x + nodeW / 2;
          const startY = srcPos.y;
          const endX = tgtPos.x + (step.name?.includes?.('Vendor') ? nodeW * 0.7 : nodeW * 0.35);
          const endY = tgtPos.y + nodeH;

          if (step.name.toLowerCase().includes('escalat')) {
            // Escalated -> goes right and up into Resolved (as in Image)
            const escStartX = srcPos.x + nodeW;
            const escStartY = srcPos.y + nodeH / 2;
            const resEndX = tgtPos.x + nodeW * 0.6;
            const resEndY = tgtPos.y + nodeH;
            pathD = `M ${escStartX} ${escStartY} L ${resEndX} ${escStartY} L ${resEndX} ${resEndY}`;
            labelX = (escStartX + resEndX) / 2;
            labelY = escStartY - 10;
          } else {
            // Pending Customer / Vendor -> goes up to In Progress
            pathD = `M ${startX} ${startY} L ${startX} ${startY - 40} L ${endX} ${startY - 40} L ${endX} ${endY}`;
            labelX = (startX + endX) / 2;
            labelY = startY - 45;
          }
        } else {
          // General curved orthogonal route
          const startX = srcPos.x + nodeW;
          const startY = srcPos.y + nodeH / 2;
          const endX = tgtPos.x;
          const endY = tgtPos.y + nodeH / 2;
          const midX = (startX + endX) / 2;
          pathD = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
          labelX = midX;
          labelY = (startY + endY) / 2;
        }

        list.push({
          id: `${step.id}-${act.id}-${targetStep.id}`,
          sourceStepName: step.name,
          targetStepName: targetStep.name,
          actionName: act.buttonName,
          actionId: act.id,
          sourceStepId: step.id,
          isDashed,
          pathD,
          arrowColor,
          labelX,
          labelY,
        });
      });
    });

    return list;
  }, [currentWf, nodePositions]);

  // Handlers for Stage Management
  const handleAddStage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStepForm.name) return;

    const newStep: WorkflowStep = {
      id: `step-${Date.now()}`,
      name: newStepForm.name,
      type: newStepForm.type,
      description: newStepForm.description || 'Custom ticket lifecycle stage.',
      actions: [],
      slaTimeoutHours: newStepForm.slaTimeoutHours,
    };

    const updatedWf = {
      ...currentWf,
      steps: [...currentWf.steps, newStep],
    };

    // Position new stage nicely
    const currentCount = currentWf.steps.length;
    setNodePositions((prev) => ({
      ...prev,
      [newStep.name]: { x: 50 + (currentCount % 4) * 230, y: 50 + Math.floor(currentCount / 4) * 160 },
    }));

    saveWorkflowUpdates(updatedWf);
    setShowAddStepModal(false);
    setNewStepForm({ name: '', type: 'in_progress', description: '', slaTimeoutHours: 8 });
  };

  const handleDeleteStage = async (stepId: string, stepName: string) => {
    if (currentWf.steps.length <= 2) {
      toast.error('A workflow must have at least 2 steps');
      return;
    }
    if (!window.confirm(`Delete stage "${stepName}" and all its wiring?`)) return;

    // Filter out deleted step and any actions on remaining steps that point to it
    const remainingSteps = currentWf.steps
      .filter((s) => String(s.id) !== String(stepId) && s.name.toLowerCase() !== stepName.toLowerCase())
      .map((s) => ({
        ...s,
        actions: (s.actions || []).filter(
          (a) =>
            String(a.targetStepId) !== String(stepId) &&
            (a.targetStepName || '').toLowerCase() !== stepName.toLowerCase()
        ),
      }));

    const updatedWf = {
      ...currentWf,
      steps: remainingSteps,
    };

    // Clean up node positions
    setNodePositions((prev) => {
      const next = { ...prev };
      delete next[stepName];
      return next;
    });

    if (selectedStepId === stepId || selectedStepId === stepName) setSelectedStepId(null);
    if (editingStep && (String(editingStep.id) === String(stepId) || editingStep.name.toLowerCase() === stepName.toLowerCase())) {
      setEditingStep(null);
    }

    // Immediately remove from database stage table if persisted
    if (!isNaN(Number(stepId)) && Number(stepId) > 0) {
      void deleteWorkflowStage(currentWf.id, stepId);
    }

    saveWorkflowUpdates(updatedWf);
  };

  // Handlers for Action Button Management
  const handleOpenAddActionModal = (statusName: string) => {
    setTargetStatusForNewAction(statusName);
    setActionForm({
      actionButtonId: '',
      actionCode: '',
      name: '',
      buttonName: '',
      targetStepName: currentWf.steps.find((s) => s.name !== statusName)?.name || 'In Progress',
      buttonStyle: 'secondary',
      lineStyle: 'solid',
      requiresNote: false,
      requiresApproval: false,
      autoTrigger: 'none',
      autoAfterDays: 3,
    });
    setShowAddActionModal(true);
  };

  const handleSaveNewAction = (e: React.FormEvent) => {
    e.preventDefault();
    if (!actionForm.buttonName || !actionForm.buttonName.trim()) {
      toast.error('Button label is required');
      return;
    }

    const stepIndex = currentWf.steps.findIndex((s) => s.name.toLowerCase() === targetStatusForNewAction.toLowerCase());
    if (stepIndex === -1) return;

    const targetStep = currentWf.steps.find((s) => s.name === actionForm.targetStepName);
    const resolvedTargetId = actionForm.targetStepName === targetStatusForNewAction
      ? 'remain'
      : (targetStep?.id || 'remain');

    const newAct: WorkflowStepAction = {
      id: `act-${Date.now()}`,
      actionButtonId: actionForm.actionButtonId || undefined,
      actionCode: actionForm.actionCode || actionForm.buttonName.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_'),
      name: actionForm.name || actionForm.buttonName.trim(),
      buttonName: actionForm.buttonName.trim(),
      actionType: 'agent_action',
      targetStepId: resolvedTargetId,
      targetStepName: actionForm.targetStepName,
      buttonStyle: actionForm.buttonStyle,
      lineStyle: actionForm.lineStyle,
      requiresNote: actionForm.requiresNote,
      requiresApproval: actionForm.requiresApproval,
      autoTrigger: actionForm.autoTrigger,
      autoAfterDays: actionForm.autoAfterDays,
    };

    const nextSteps = [...currentWf.steps];
    nextSteps[stepIndex] = {
      ...nextSteps[stepIndex],
      actions: [...nextSteps[stepIndex].actions, newAct],
    };

    saveWorkflowUpdates({ ...currentWf, steps: nextSteps });
    setShowAddActionModal(false);
    toast.success(`Action "${newAct.buttonName}" added to stage "${targetStatusForNewAction}"`);
  };

  const handleDeleteAction = (stepId: string, actionId: string) => {
    const step = currentWf.steps.find((s) => s.id === stepId);
    const act = step?.actions.find((a) => a.id === actionId);
    const actLabel = act?.buttonName || 'Action button';

    const nextSteps = currentWf.steps.map((s) => {
      if (s.id === stepId) {
        return {
          ...s,
          actions: s.actions.filter((a) => a.id !== actionId),
        };
      }
      return s;
    });

    saveWorkflowUpdates({ ...currentWf, steps: nextSteps });
    toast.success(`Removed "${actLabel}" from stage "${step?.name || ''}"`);
  };

  const handleEditActionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAction) return;

    const nextSteps = currentWf.steps.map((s) => {
      if (s.id === editingAction.stepId) {
        return {
          ...s,
          actions: s.actions.map((a) => (a.id === editingAction.action.id ? { ...editingAction.action } : a)),
        };
      }
      return s;
    });

    saveWorkflowUpdates({ ...currentWf, steps: nextSteps });
    setShowEditActionModal(false);
    setEditingAction(null);
    toast.success(`Action button "${editingAction.action.buttonName}" updated`);
  };

  // Handlers for Workflow Settings & Creation
  const handleOpenWorkflowSettings = () => {
    if (!currentWf) return;
    setWorkflowSettingsForm({
      name: currentWf.name,
      code: currentWf.code,
      description: currentWf.description || '',
      allowedTicketTypes: Array.isArray(currentWf.allowedTicketTypes) && currentWf.allowedTicketTypes.length > 0
        ? [...currentWf.allowedTicketTypes]
        : [currentWf.ticketType || selectedTypeObj?.name || 'Incident'],
      isDefault: Boolean(currentWf.isDefault),
    });
    setShowWorkflowSettingsModal(true);
  };

  const handleSaveWorkflowSettings = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentWf) return;
    if (!workflowSettingsForm.name.trim()) {
      toast.error('Workflow name is required');
      return;
    }
    const updated: ExtendedWorkflow = {
      ...currentWf,
      name: workflowSettingsForm.name.trim(),
      code: workflowSettingsForm.code.trim() || currentWf.code,
      description: workflowSettingsForm.description.trim(),
      allowedTicketTypes: workflowSettingsForm.allowedTicketTypes.length > 0
        ? workflowSettingsForm.allowedTicketTypes
        : [selectedTypeObj?.name || 'Incident'],
      isDefault: workflowSettingsForm.isDefault,
    };
    saveWorkflowUpdates(updated);
    setShowWorkflowSettingsModal(false);
  };

  const handleOpenCreateWorkflow = () => {
    const typeName = selectedTypeObj?.name || 'Custom';
    setCreateWorkflowForm({
      name: `${typeName} Custom Workflow`,
      code: `WF-${typeName.slice(0, 3).toUpperCase()}-0${workflowsForSelectedType.length + 1}`,
      description: `Custom workflow for ${typeName}`,
      allowedTicketTypes: [typeName],
      cloneCurrent: true,
      isDefault: false,
    });
    setShowCreateWorkflowModal(true);
  };

  const handleSaveCreateWorkflow = (e: React.FormEvent) => {
    e.preventDefault();
    if (!createWorkflowForm.name.trim()) {
      toast.error('Workflow name is required');
      return;
    }

    const typeName = selectedTypeObj?.name || 'Custom';
    const newId = `wf-${Date.now()}`;
    const baseSteps = createWorkflowForm.cloneCurrent && currentWf && currentWf.steps.length > 0
      ? JSON.parse(JSON.stringify(currentWf.steps))
      : [
          {
            id: `step-${Date.now()}-1`,
            name: 'New',
            type: 'initial' as const,
            description: 'Ticket received and awaiting review',
            actions: [
              { id: `act-${Date.now()}-1`, name: 'Start Work', buttonName: 'Start Work', actionType: 'agent_action' as const, targetStepId: 'in-progress', targetStepName: 'In Progress', buttonStyle: 'primary' as const },
            ]
          },
          {
            id: `step-${Date.now()}-2`,
            name: 'In Progress',
            type: 'in_progress' as const,
            description: 'Work in progress',
            actions: [
              { id: `act-${Date.now()}-2`, name: 'Resolve', buttonName: 'Resolve', actionType: 'agent_action' as const, targetStepId: 'resolved', targetStepName: 'Resolved', buttonStyle: 'success' as const },
            ]
          },
          {
            id: `step-${Date.now()}-3`,
            name: 'Resolved',
            type: 'resolved' as const,
            description: 'Ticket resolved',
            actions: [
              { id: `act-${Date.now()}-3`, name: 'Close', buttonName: 'Close', actionType: 'agent_action' as const, targetStepId: 'closed', targetStepName: 'Closed', buttonStyle: 'outline' as const },
            ]
          },
          {
            id: `step-${Date.now()}-4`,
            name: 'Closed',
            type: 'closed' as const,
            description: 'Ticket closed and archived',
            actions: []
          }
        ];

    const newWf: ExtendedWorkflow = {
      id: newId,
      name: createWorkflowForm.name.trim(),
      code: createWorkflowForm.code.trim() || `WF-${Date.now()}`,
      ticketType: typeName,
      allowedTicketTypes: createWorkflowForm.allowedTicketTypes.length > 0 ? createWorkflowForm.allowedTicketTypes : [typeName],
      description: createWorkflowForm.description.trim() || `Workflow for ${typeName}`,
      isDefault: createWorkflowForm.isDefault,
      isActive: true,
      workingHoursSchedule: 'Mon-Fri 09:00 - 18:00 IST',
      allowedTeams: ['Support Team'],
      steps: baseSteps,
    };

    saveWorkflowUpdates(newWf);
    setSelectedWorkflowId(newId);
    setShowCreateWorkflowModal(false);
    toast.success(`Workflow "${newWf.name}" created!`);
  };

  if (!currentWf) {
    return (
      <div className="rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground shadow-xs">
        <GitBranch className="size-8 mx-auto mb-3 text-primary animate-pulse" />
        <h3 className="font-semibold text-sm text-foreground">Loading Workflows from Database</h3>
        <p className="text-xs mt-1">Retrieving ticket types and professional lifecycle workflows...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-foreground font-sans">
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 1. TOP HEADER: DROPDOWN SELECTOR & CONTROLS                    */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-xs space-y-3">
        {/* LINE 1: DROPDOWNS & WORKFLOW INFO */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border/60 pb-3">
          <div className="flex flex-wrap items-center gap-4">
            {/* TICKET TYPE DROPDOWN SELECTOR */}
            <div className="flex items-center gap-2.5">
              <label htmlFor="ticket-type-select" className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 uppercase tracking-wider">
                <Tag className="size-3.5 text-primary" /> Ticket Type:
              </label>
              <select
                id="ticket-type-select"
                value={selectedTicketTypeId}
                onChange={(e) => {
                  setSelectedTicketTypeId(e.target.value);
                  setSelectedWorkflowId('');
                }}
                className="h-9 px-3 py-1.5 rounded-lg border border-border bg-background font-semibold text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer min-w-[220px] shadow-2xs"
              >
                {ticketTypesList.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {/* WORKFLOW DROPDOWN SELECTOR (SUPPORTS MULTIPLE WORKFLOWS PER TICKET TYPE) */}
            <div className="flex items-center gap-2">
              <label htmlFor="workflow-select" className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 uppercase tracking-wider">
                <GitBranch className="size-3.5 text-primary" /> Workflow:
              </label>
              <select
                id="workflow-select"
                value={currentWf.id}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '__new__') {
                    handleOpenCreateWorkflow();
                  } else {
                    setSelectedWorkflowId(val);
                  }
                }}
                className="h-9 px-3 py-1.5 rounded-lg border border-border bg-background font-semibold text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer min-w-[240px] shadow-2xs"
              >
                {workflowsForSelectedType.length > 0 ? (
                  workflowsForSelectedType.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name} {w.isDefault ? '(Default)' : ''}
                    </option>
                  ))
                ) : (
                  <option value={currentWf.id}>{currentWf.name} (Default)</option>
                )}
                <optgroup label="Actions & Other Workflows">
                  <option value="__new__">+ Create New Workflow for {selectedTypeObj?.name || 'Ticket Type'}...</option>
                  {workflows
                    .filter((w) => !workflowsForSelectedType.some((wf) => wf.id === w.id))
                    .map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name} ({w.ticketType || 'System'})
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>
          </div>

          {/* Workflow Info Breadcrumb on Line 1 */}
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleOpenWorkflowSettings}
              className="inline-flex items-center gap-1 rounded-md font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              aria-label={`Open ${currentWf.name} workflow details`}
            >
              {currentWf.name}<ChevronRight className="size-3.5" />
            </button> &bull; {currentWf.steps.length} Stages &bull; {renderedConnections.length} Wires
          </div>
        </div>

        {/* LINE 2: ALL ACTION BUTTONS */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-0.5">
          <div className="flex flex-wrap items-center gap-2">
            {/* New Workflow button */}
            <button
              type="button"
              onClick={handleOpenCreateWorkflow}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted text-xs font-semibold text-foreground transition-all cursor-pointer shadow-2xs"
              title={`Create a new workflow for ${selectedTypeObj?.name || 'Ticket Type'}`}
            >
              <Plus className="size-3.5 text-primary" /> New Workflow
            </button>

            <div className="h-5 w-px bg-border mx-1 hidden sm:block" />

            {/* Drawing / Wiring Mode Toggle */}
            <button
              type="button"
              onClick={() => {
                setIsDrawingMode(!isDrawingMode);
                toast(isDrawingMode ? 'Wire drawing mode disabled' : 'Wire Drawing Mode: Drag from node ports (+) to connect stages');
              }}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer border shadow-2xs",
                isDrawingMode
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border text-foreground hover:bg-muted"
              )}
              title="Click and drag from any stage port (+) to draw connecting flow lines"
            >
              <Link2 className="size-3.5" />
              {isDrawingMode ? 'Drawing Wires Active' : 'Draw / Wire Flow'}
            </button>

            {/* Add Stage */}
            <button
              type="button"
              onClick={() => setShowAddStepModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-border text-foreground hover:bg-muted text-xs font-semibold shadow-2xs cursor-pointer"
            >
              <Plus className="size-3.5 text-primary" /> Add Stage
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Auto-Layout */}
            <button
              type="button"
              onClick={() => {
                // Compute clean DAG / layered horizontal auto-layout
                const steps = currentWf?.steps || [];
                const newPositions: Record<string, { x: number; y: number }> = {};
                steps.forEach((step, idx) => {
                  const sName = (step.name || '').toLowerCase();
                  if (sName.includes('new') || sName.includes('open') || sName.includes('draft')) {
                    newPositions[step.name] = { x: 40, y: 45 };
                  } else if (sName.includes('assign') || sName.includes('triage')) {
                    newPositions[step.name] = { x: 240, y: 45 };
                  } else if (sName.includes('progress') || sName.includes('investigat') || sName.includes('work')) {
                    newPositions[step.name] = { x: 460, y: 45 };
                  } else if (sName.includes('resolv') || sName.includes('fulfill')) {
                    newPositions[step.name] = { x: 690, y: 45 };
                  } else if (sName.includes('close') || sName.includes('complet')) {
                    newPositions[step.name] = { x: 910, y: 45 };
                  } else if (sName.includes('customer') || sName.includes('user')) {
                    newPositions[step.name] = { x: 140, y: 220 };
                  } else if (sName.includes('vendor') || sName.includes('supplier')) {
                    newPositions[step.name] = { x: 380, y: 220 };
                  } else if (sName.includes('escalat') || sName.includes('tier')) {
                    newPositions[step.name] = { x: 620, y: 220 };
                  } else {
                    const col = idx % 4;
                    const row = Math.floor(idx / 4);
                    newPositions[step.name] = { x: 40 + col * 220, y: 45 + row * 180 };
                  }
                });
                setNodePositions(newPositions);
                if (selectedTypeObj) {
                  saveJson(`teamone_wf_positions_${selectedTypeObj.id}`, newPositions);
                }
                toast.success('Workflow canvas auto-layout applied');
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-card hover:bg-secondary text-xs font-semibold text-foreground cursor-pointer shadow-xs transition-colors"
              title="Automatically arrange workflow stages into layered flow"
            >
              <Sparkles className="size-3.5 text-primary" /> Auto-Layout
            </button>

            {/* Reset Positions */}
            <button
              type="button"
              onClick={() => {
                setNodePositions({});
                if (selectedTypeObj) {
                  saveJson(`teamone_wf_positions_${selectedTypeObj.id}`, {});
                }
                toast.success('Workflow canvas reset to standard layout');
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-card hover:bg-muted text-xs font-semibold text-muted-foreground hover:text-foreground cursor-pointer"
              title="Reset stages to default positions"
            >
              <RotateCcw className="size-3.5" /> Reset Layout
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 2. SECTION 1: DRAGGABLE & DRAWABLE WORKFLOW CANVAS (IMAGE 1)   */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="rounded-2xl border border-border bg-card shadow-xs overflow-hidden">
        {/* Canvas Header & Instructions */}
        <div className="p-4 border-b border-border bg-card flex flex-wrap items-center justify-between gap-3 text-xs">
          <div>
            <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
              Ticket Workflow
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Drag stage cards to reposition &bull; Drag from port <span className="font-mono font-semibold text-blue-600">(+)</span> to draw connecting flow lines &bull; Dashed lines indicate special hold/escalation routes.
            </p>
          </div>

          <div className="flex items-center gap-2 bg-muted/40 p-1 rounded-lg border border-border">
            <button
              type="button"
              onClick={() => setCanvasZoom((prev) => Math.max(0.7, +(prev - 0.1).toFixed(1)))}
              className="p-1 rounded hover:bg-card text-muted-foreground hover:text-foreground cursor-pointer"
              title="Zoom Out"
            >
              <ZoomOut className="size-3.5" />
            </button>
            <span className="text-xs font-mono font-semibold px-1 text-foreground">
              {Math.round(canvasZoom * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setCanvasZoom((prev) => Math.min(1.4, +(prev + 0.1).toFixed(1)))}
              className="p-1 rounded hover:bg-card text-muted-foreground hover:text-foreground cursor-pointer"
              title="Zoom In"
            >
              <ZoomIn className="size-3.5" />
            </button>
          </div>
        </div>

        {/* INTERACTIVE DRAGGABLE & DRAWABLE SVG CANVAS */}
        <div
          ref={canvasRef}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          className="relative min-h-[420px] h-[460px] bg-slate-50/70 dark:bg-slate-900/40 overflow-hidden cursor-default relative"
          style={{
            backgroundImage: 'radial-gradient(circle, var(--border) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        >
          {/* SVG LAYER: FLOW LINES, ARROWS, AND DASHED SPECIAL CONNECTIONS */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none z-10"
            style={{ transform: `scale(${canvasZoom})`, transformOrigin: '0 0' }}
          >
            <defs>
              {/* Solid Arrow Marker */}
              <marker
                id="flow-arrow-solid"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 1.5 L 9 5 L 0 8.5 z" fill="#475569" />
              </marker>

              {/* Dashed Blue/Slate Special Marker */}
              <marker
                id="flow-arrow-dashed"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 1.5 L 9 5 L 0 8.5 z" fill="#64748b" />
              </marker>

              {/* Active Drawing Marker */}
              <marker
                id="flow-arrow-active"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M 0 1.5 L 9 5 L 0 8.5 z" fill="#2563eb" />
              </marker>
            </defs>

            {/* RENDERED STATIC & DYNAMIC CONNECTION FLOW LINES */}
            {renderedConnections.map((conn) => {
              const isSelected = selectedLineId === conn.id;

              return (
                <g key={conn.id} className="cursor-pointer pointer-events-auto">
                  {/* Transparent hover hit target */}
                  <path
                    d={conn.pathD}
                    fill="none"
                    stroke="transparent"
                    strokeWidth="14"
                    onClick={() => setSelectedLineId(isSelected ? null : conn.id)}
                  />

                  {/* Visual Line */}
                  <path
                    d={conn.pathD}
                    fill="none"
                    stroke={isSelected ? '#2563eb' : conn.isDashed ? '#64748b' : '#475569'}
                    strokeWidth={isSelected ? '2.5' : '2'}
                    strokeDasharray={conn.isDashed ? '5 4' : 'none'}
                    markerEnd={conn.isDashed ? 'url(#flow-arrow-dashed)' : 'url(#flow-arrow-solid)'}
                    className="transition-colors duration-150"
                  />

                  {/* Action Label Bubble on Line */}
                  {isSelected && (
                    <g transform={`translate(${conn.labelX}, ${conn.labelY})`}>
                      <rect
                        x="-55"
                        y="-12"
                        width="110"
                        height="24"
                        rx="6"
                        fill="var(--card)"
                        stroke="var(--primary)"
                        strokeWidth="1.5"
                      />
                      <text
                        x="0"
                        y="4"
                        textAnchor="middle"
                        fontSize="10"
                        fontWeight="bold"
                        fill="var(--foreground)"
                      >
                        {conn.actionName.slice(0, 16)}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}

            {/* ACTIVE WIRING LINE BEING DRAWN */}
            {activeWiring && (
              <path
                d={`M ${activeWiring.startX} ${activeWiring.startY} L ${activeWiring.currentX} ${activeWiring.currentY}`}
                fill="none"
                stroke="#2563eb"
                strokeWidth="2.5"
                strokeDasharray="4 4"
                markerEnd="url(#flow-arrow-active)"
              />
            )}
          </svg>

          {/* DRAGGABLE NODE CARDS LAYER */}
          <div
            className="absolute inset-0 z-20 pointer-events-auto"
            style={{ transform: `scale(${canvasZoom})`, transformOrigin: '0 0' }}
          >
            {currentWf.steps.map((step, idx) => {
              const pos = getNodePos(step.name, idx);
              const pillClass = getNodePillStyle(step.name);
              const isSelected = selectedStepId === step.id;
              const isHovered = hoveredNodeId === step.id;
              const isBranch = step.name.toLowerCase().includes('pending') || step.name.toLowerCase().includes('escalat');

              return (
                <div
                  key={step.id}
                  style={{
                    position: 'absolute',
                    left: `${pos.x}px`,
                    top: `${pos.y}px`,
                    width: `${NODE_WIDTH}px`,
                    height: `${NODE_HEIGHT}px`,
                  }}
                  onMouseDown={(e) => handleNodeMouseDown(e, step.name)}
                  onMouseEnter={() => setHoveredNodeId(step.id)}
                  onMouseLeave={() => setHoveredNodeId(null)}
                  onMouseUp={() => handleNodeDropWire(step)}
                  className="group"
                >
                  {/* STAGE CARD */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedStepId(step.id);
                      setEditingStep(step);
                    }}
                    className={cn(
                      "w-full h-full rounded-lg transition-all duration-150 cursor-grab active:cursor-grabbing select-none flex flex-col justify-center px-3.5 py-1.5 border border-border bg-card shadow-xs",
                      pillClass,
                      isBranch && "border-dashed",
                      isSelected ? "ring-2 ring-primary border-primary shadow-sm" : "hover:shadow-md hover:border-primary/50"
                    )}
                  >
                    <span className="font-semibold text-foreground text-xs leading-tight line-clamp-2" title={step.name}>
                      {step.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground mt-0.5 font-medium">
                      {isBranch ? 'Branch route' : 'Lifecycle stage'}
                    </span>
                  </div>

                  {/* WIRE CONNECTOR PORTS (REVEALED ON HOVER OR IN DRAWING MODE) */}
                  {(isDrawingMode || isHovered) && (
                    <>
                      {/* Top Port */}
                      <button
                        type="button"
                        onMouseDown={(e) => handlePortMouseDown(e, step, 'top')}
                        className="absolute -top-2.5 left-1/2 -translate-x-1/2 size-5 rounded-full bg-primary text-primary-foreground border-2 border-background flex items-center justify-center shadow-xs hover:scale-125 cursor-crosshair z-30 transition-transform"
                        title="Draw wire from top port"
                      >
                        <Plus className="size-3 stroke-[3]" />
                      </button>

                      {/* Right Port */}
                      <button
                        type="button"
                        onMouseDown={(e) => handlePortMouseDown(e, step, 'right')}
                        className="absolute top-1/2 -right-2.5 -translate-y-1/2 size-5 rounded-full bg-primary text-primary-foreground border-2 border-background flex items-center justify-center shadow-xs hover:scale-125 cursor-crosshair z-30 transition-transform"
                        title="Draw wire from right port"
                      >
                        <Plus className="size-3 stroke-[3]" />
                      </button>

                      {/* Bottom Port */}
                      <button
                        type="button"
                        onMouseDown={(e) => handlePortMouseDown(e, step, 'bottom')}
                        className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 size-5 rounded-full bg-primary text-primary-foreground border-2 border-background flex items-center justify-center shadow-xs hover:scale-125 cursor-crosshair z-30 transition-transform"
                        title="Draw wire from bottom port"
                      >
                        <Plus className="size-3 stroke-[3]" />
                      </button>

                      {/* Left Port */}
                      <button
                        type="button"
                        onMouseDown={(e) => handlePortMouseDown(e, step, 'left')}
                        className="absolute top-1/2 -left-2.5 -translate-y-1/2 size-5 rounded-full bg-primary text-primary-foreground border-2 border-background flex items-center justify-center shadow-xs hover:scale-125 cursor-crosshair z-30 transition-transform"
                        title="Draw wire from left port"
                      >
                        <Plus className="size-3 stroke-[3]" />
                      </button>
                    </>
                  )}

                  {/* Quick Delete Stage Button */}
                  {!isDrawingMode && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteStage(step.id, step.name);
                      }}
                      className="absolute -top-1.5 -right-1.5 size-4.5 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-rose-700 shadow-xs cursor-pointer z-30 transition-opacity"
                      title="Delete stage"
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 3. SECTION 2: ACTION BUTTONS MATRIX (IMAGE 2)                  */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div>
            <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
              Action Buttons
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Available action buttons and state transitions by status for <strong className="text-foreground">{selectedTicketType}</strong> tickets.
            </p>
          </div>

          <div className="text-xs text-muted-foreground">
            Click any button to customize &bull; Click <span className="font-semibold text-foreground">+ Add Action</span> to extend.
          </div>
        </div>

        {/* ACTION BUTTONS TABLE */}
        <div className="rounded-xl border border-border overflow-hidden bg-card shadow-2xs">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-muted-foreground font-semibold uppercase text-xs tracking-wider">
                <th className="py-3 px-5 w-48 sm:w-60">Status</th>
                <th className="py-3 px-5">Available Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {currentWf.steps.map((step) => {
                const statusBadgeClass = getStatusBadgeClass(step.name);

                return (
                  <tr key={step.id} className="hover:bg-accent/30 transition-colors">
                    {/* STATUS COLUMN */}
                    <td className="py-3 px-5 align-top">
                      <span className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold", statusBadgeClass)}>
                        {step.name}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1 leading-tight">
                        {step.description || 'Standard status phase.'}
                      </p>
                    </td>

                    {/* AVAILABLE ACTIONS COLUMN */}
                    <td className="py-4 px-5 align-middle">
                      <div className="flex flex-wrap items-center gap-2">
                        {step.actions.map((act) => {
                          const btnStyleClass = getActionButtonStyle(act.buttonName, act.buttonStyle);

                          return (
                            <div key={act.id} className="relative group inline-flex items-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingAction({ stepId: step.id, action: { ...act } });
                                  setShowEditActionModal(true);
                                }}
                                className={cn(
                                  "px-3.5 py-1.5 rounded-lg text-xs transition-all duration-150 select-none flex items-center gap-1.5",
                                  "cursor-pointer active:scale-95",
                                  btnStyleClass
                                )}
                                title={`Edit ${act.buttonName}: target ${act.targetStepName || 'Next'}`}
                              >
                                <span>{act.buttonName}</span>
                              </button>

                              {/* Hover Delete Action Button */}
                              {(
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteAction(step.id, act.id);
                                  }}
                                  className="absolute -top-1.5 -right-1.5 size-4 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-rose-700 shadow-xs cursor-pointer transition-opacity"
                                  title="Remove action button"
                                >
                                  <X className="size-2.5" />
                                </button>
                              )}
                            </div>
                          );
                        })}

                        {/* ADD ACTION BUTTON TRIGGER */}
                        {(
                          <button
                            type="button"
                            onClick={() => handleOpenAddActionModal(step.name)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-dashed border-border bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted hover:border-foreground transition-colors cursor-pointer"
                            title={`Add new action button for status ${step.name}`}
                          >
                            <Plus className="size-3 text-primary" />
                            <span>Add Action</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>


      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 5. MODALS                                                      */}
      {/* ═══════════════════════════════════════════════════════════════ */}

      {/* MODAL 1: ADD NEW STAGE DRAWER */}
      {showAddStepModal && (
        <WorkflowDrawer title={`Add stage to ${selectedTicketType}`} onClose={() => setShowAddStepModal(false)}>
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <h3 className="font-display text-card-title text-foreground">Add Stage to {selectedTicketType}</h3>
              <button onClick={() => setShowAddStepModal(false)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleAddStage} className="flex-1 flex flex-col overflow-hidden text-xs">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="font-semibold text-foreground block mb-1">Stage Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. QA Verification or Waiting on Vendor"
                    value={newStepForm.name}
                    onChange={(e) => setNewStepForm({ ...newStepForm, name: e.target.value })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-medium text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Stage Category</label>
                  <select
                    value={newStepForm.type}
                    onChange={(e) => setNewStepForm({ ...newStepForm, type: e.target.value as WorkflowStep['type'] })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="initial">Initial / Logged (New)</option>
                    <option value="in_progress">In Progress / Investigation</option>
                    <option value="pending">Pending / On Hold (Customer/Vendor)</option>
                    <option value="approval">Escalation / Approval Tier</option>
                    <option value="resolved">Resolved / Implemented</option>
                    <option value="closed">Closed / Archived</option>
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">SLA Target Resolution Time (Hours)</label>
                  <input
                    type="number"
                    min={1}
                    value={newStepForm.slaTimeoutHours}
                    onChange={(e) => setNewStepForm({ ...newStepForm, slaTimeoutHours: Number(e.target.value) })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Description</label>
                  <textarea
                    rows={2}
                    value={newStepForm.description}
                    onChange={(e) => setNewStepForm({ ...newStepForm, description: e.target.value })}
                    placeholder="Lifecycle stage guidelines..."
                    className="w-full p-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddStepModal(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer shadow-xs"
                >
                  Add Stage
                </button>
              </div>
            </form>
        </WorkflowDrawer>
      )}

      {/* MODAL 2: ADD ACTION BUTTON DRAWER */}
      {showAddActionModal && (
        <WorkflowDrawer title={`Add action for ${targetStatusForNewAction}`} onClose={() => setShowAddActionModal(false)}>
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <h3 className="font-display text-card-title text-foreground">
                Add Action Button for Status "{targetStatusForNewAction}"
              </h3>
              <button onClick={() => setShowAddActionModal(false)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveNewAction} className="flex-1 flex flex-col overflow-hidden text-xs">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="font-semibold text-foreground block mb-1">Select Action Button (ITSM Catalog)</label>
                  <select
                    value={actionForm.actionButtonId || ''}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const matched = (storeActions || []).find((a) => String(a.id) === selectedId || a.code === selectedId);
                      if (matched) {
                        setActionForm({
                          ...actionForm,
                          actionButtonId: String(matched.id),
                          actionCode: matched.code || matched.systemUse,
                          buttonName: matched.buttonName || actionForm.buttonName,
                          name: matched.buttonName || actionForm.buttonName,
                          buttonStyle: (matched.code?.includes('REJECT') || matched.code?.includes('FAIL') || matched.actionCategory === 'destructive')
                            ? 'destructive'
                            : (matched.code?.includes('RESOLVE') || matched.code?.includes('APPROVE'))
                            ? 'success'
                            : (matched.code?.includes('HOLD') || matched.code?.includes('REOPEN'))
                            ? 'warning'
                            : matched.isQuickAction
                            ? 'primary'
                            : 'secondary',
                          requiresNote: Boolean(matched.requiresNote || matched.requiresComment),
                          requiresApproval: Boolean(matched.requiresApproval || matched.code?.includes('APPROVE')),
                        });
                      } else {
                        setActionForm({ ...actionForm, actionButtonId: '', actionCode: '' });
                      }
                    }}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="">-- Choose Existing Action Button or Create Custom --</option>
                    {(storeActions || []).map((btn) => (
                      <option key={btn.id} value={btn.id}>
                        {btn.buttonName} ({btn.code || btn.systemUse})
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Selecting a catalog action button auto-populates label, color style, and flags.</p>
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Button Label *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Start Work, Escalate, Request Info"
                    value={actionForm.buttonName}
                    onChange={(e) => setActionForm({ ...actionForm, buttonName: e.target.value, name: e.target.value })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-semibold text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Target Status After Action</label>
                  <select
                    value={actionForm.targetStepName}
                    onChange={(e) => setActionForm({ ...actionForm, targetStepName: e.target.value })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value={targetStatusForNewAction}>Remain in Current Status ({targetStatusForNewAction})</option>
                    {currentWf.steps.map((s) => (
                      <option key={s.id} value={s.name}>Move to: {s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Transition Wire Line Style</label>
                    <select
                      value={actionForm.lineStyle}
                      onChange={(e) => setActionForm({ ...actionForm, lineStyle: e.target.value as any })}
                      className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="solid">Solid Line (Standard Sequential Flow)</option>
                      <option value="dashed">Dashed Line (Conditional / Exception Wire)</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-semibold text-foreground block mb-1">Button Color Style</label>
                    <select
                      value={actionForm.buttonStyle}
                      onChange={(e) => setActionForm({ ...actionForm, buttonStyle: e.target.value as any })}
                      className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="primary">Solid Blue (Primary)</option>
                      <option value="secondary">Soft Light Blue (Standard Action)</option>
                      <option value="success">Soft Emerald Green (Resolve / Approve)</option>
                      <option value="destructive">Soft Rose Red (Escalate / Reject)</option>
                      <option value="warning">Soft Amber (Hold / Reopen)</option>
                      <option value="outline">Soft Slate / White Border (Add Note / Cancel)</option>
                    </select>
                  </div>
                </div>

                <div className="p-3 bg-muted/40 rounded-lg border border-border space-y-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={actionForm.requiresNote}
                      onChange={(e) => setActionForm({ ...actionForm, requiresNote: e.target.checked })}
                      className="size-4 rounded border-border"
                    />
                    <span>Prompt for Internal Note / Justification when clicked</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs font-semibold text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={actionForm.requiresApproval}
                      onChange={(e) => setActionForm({ ...actionForm, requiresApproval: e.target.checked })}
                      className="size-4 rounded border-border"
                    />
                    <span>Requires Designated Approver Authorization (Approval Gate)</span>
                  </label>
                </div>

                <div className="space-y-2 pt-1 border-t border-border">
                  <label className="font-semibold text-foreground block mb-1">Auto-Trigger Transition Rule</label>
                  <select
                    value={actionForm.autoTrigger}
                    onChange={(e) => setActionForm({ ...actionForm, autoTrigger: e.target.value as any })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="none">None (Manual Agent / Approver Action Only)</option>
                    <option value="customer_reply">Automatic on Customer Reply / Reopen</option>
                    <option value="after_days">Automatic after Inactivity Days (SLA Auto-Close)</option>
                  </select>
                  {actionForm.autoTrigger === 'after_days' && (
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Auto-transition after:</span>
                      <input
                        type="number"
                        min={1}
                        max={30}
                        value={actionForm.autoAfterDays}
                        onChange={(e) => setActionForm({ ...actionForm, autoAfterDays: Math.max(1, parseInt(e.target.value) || 1) })}
                        className="w-20 h-7 px-2 rounded border border-input bg-background text-xs font-mono"
                      />
                      <span className="text-xs text-muted-foreground">days of inactivity</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddActionModal(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer shadow-xs"
                >
                  Save Action Button
                </button>
              </div>
            </form>
        </WorkflowDrawer>
      )}

      {/* MODAL 3: EDIT ACTION BUTTON DRAWER */}
      {showEditActionModal && editingAction && (
        <WorkflowDrawer title="Edit action button" onClose={() => { setShowEditActionModal(false); setEditingAction(null); }}>
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <h3 className="font-display text-card-title text-foreground">Edit Action Button</h3>
              <button
                onClick={() => {
                  setShowEditActionModal(false);
                  setEditingAction(null);
                }}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleEditActionSubmit} className="flex-1 flex flex-col overflow-hidden text-xs">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="font-semibold text-foreground block mb-1">Select Action Button (ITSM Catalog)</label>
                  <select
                    value={editingAction.action.actionButtonId || ''}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const matched = (storeActions || []).find((a) => String(a.id) === selectedId || a.code === selectedId);
                      if (matched) {
                        setEditingAction({
                          ...editingAction,
                          action: {
                            ...editingAction.action,
                            actionButtonId: String(matched.id),
                            actionCode: matched.code || matched.systemUse,
                            buttonName: matched.buttonName || editingAction.action.buttonName,
                            name: matched.buttonName || editingAction.action.buttonName,
                            buttonStyle: (matched.code?.includes('REJECT') || matched.code?.includes('FAIL') || matched.actionCategory === 'destructive')
                              ? 'destructive'
                              : (matched.code?.includes('RESOLVE') || matched.code?.includes('APPROVE'))
                              ? 'success'
                              : (matched.code?.includes('HOLD') || matched.code?.includes('REOPEN'))
                              ? 'warning'
                              : matched.isQuickAction
                              ? 'primary'
                              : 'secondary',
                            requiresNote: Boolean(matched.requiresNote || matched.requiresComment),
                            requiresApproval: Boolean(matched.requiresApproval || matched.code?.includes('APPROVE')),
                          },
                        });
                      } else {
                        setEditingAction({
                          ...editingAction,
                          action: { ...editingAction.action, actionButtonId: undefined, actionCode: undefined },
                        });
                      }
                    }}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="">-- Choose Existing Action Button or Keep Custom --</option>
                    {(storeActions || []).map((btn) => (
                      <option key={btn.id} value={btn.id}>
                        {btn.buttonName} ({btn.code || btn.systemUse})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Button Label *</label>
                  <input
                    type="text"
                    required
                    value={editingAction.action.buttonName}
                    onChange={(e) =>
                      setEditingAction({
                        ...editingAction,
                        action: { ...editingAction.action, buttonName: e.target.value, name: e.target.value },
                      })
                    }
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-semibold text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Target Status After Action</label>
                  <select
                    value={editingAction.action.targetStepName || 'remain'}
                    onChange={(e) => {
                      const targetName = e.target.value;
                      const targetStep = currentWf.steps.find((s) => s.name === targetName);
                      setEditingAction({
                        ...editingAction,
                        action: {
                          ...editingAction.action,
                          targetStepName: targetName,
                          targetStepId: targetStep?.id || 'remain',
                        },
                      });
                    }}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="remain">Remain in Current Status</option>
                    {currentWf.steps.map((s) => (
                      <option key={s.id} value={s.name}>Move to: {s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Transition Wire Line Style</label>
                    <select
                      value={editingAction.action.lineStyle || 'solid'}
                      onChange={(e) =>
                        setEditingAction({
                          ...editingAction,
                          action: { ...editingAction.action, lineStyle: e.target.value as any },
                        })
                      }
                      className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="solid">Solid Line (Standard Sequential Flow)</option>
                      <option value="dashed">Dashed Line (Conditional / Exception Wire)</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-semibold text-foreground block mb-1">Button Color Style</label>
                    <select
                      value={editingAction.action.buttonStyle || 'secondary'}
                      onChange={(e) =>
                        setEditingAction({
                          ...editingAction,
                          action: { ...editingAction.action, buttonStyle: e.target.value as any },
                        })
                      }
                      className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="primary">Solid Blue (Primary)</option>
                      <option value="secondary">Soft Light Blue (Standard Action)</option>
                      <option value="success">Soft Emerald Green (Resolve / Approve)</option>
                      <option value="destructive">Soft Rose Red (Escalate / Reject)</option>
                      <option value="warning">Soft Amber (Hold / Reopen)</option>
                      <option value="outline">Soft Slate / White Border (Add Note / Cancel)</option>
                    </select>
                  </div>
                </div>

                <div className="p-3 bg-muted/40 rounded-lg border border-border space-y-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingAction.action.requiresNote ?? false}
                      onChange={(e) =>
                        setEditingAction({
                          ...editingAction,
                          action: { ...editingAction.action, requiresNote: e.target.checked },
                        })
                      }
                      className="size-4 rounded border-border"
                    />
                    <span>Prompt for Internal Note / Justification when clicked</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs font-semibold text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingAction.action.requiresApproval ?? false}
                      onChange={(e) =>
                        setEditingAction({
                          ...editingAction,
                          action: { ...editingAction.action, requiresApproval: e.target.checked },
                        })
                      }
                      className="size-4 rounded border-border"
                    />
                    <span>Requires Designated Approver Authorization (Approval Gate)</span>
                  </label>
                </div>

                <div className="space-y-2 pt-1 border-t border-border">
                  <label className="font-semibold text-foreground block mb-1">Auto-Trigger Transition Rule</label>
                  <select
                    value={editingAction.action.autoTrigger || 'none'}
                    onChange={(e) =>
                      setEditingAction({
                        ...editingAction,
                        action: { ...editingAction.action, autoTrigger: e.target.value as any },
                      })
                    }
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="none">None (Manual Agent / Approver Action Only)</option>
                    <option value="customer_reply">Automatic on Customer Reply / Reopen</option>
                    <option value="after_days">Automatic after Inactivity Days (SLA Auto-Close)</option>
                  </select>
                  {editingAction.action.autoTrigger === 'after_days' && (
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Auto-transition after:</span>
                      <input
                        type="number"
                        min={1}
                        max={30}
                        value={editingAction.action.autoAfterDays || 3}
                        onChange={(e) =>
                          setEditingAction({
                            ...editingAction,
                            action: { ...editingAction.action, autoAfterDays: Math.max(1, parseInt(e.target.value) || 1) },
                          })
                        }
                        className="w-20 h-7 px-2 rounded border border-input bg-background text-xs font-mono"
                      />
                      <span className="text-xs text-muted-foreground">days of inactivity</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditActionModal(false);
                    setEditingAction(null);
                  }}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer shadow-xs"
                >
                  Save Changes
                </button>
              </div>
            </form>
        </WorkflowDrawer>
      )}

      {/* MODAL 4: STAGE PROPERTY EDITOR DRAWER */}
      {editingStep && (
        <WorkflowDrawer title={`Stage properties: ${editingStep.name}`} onClose={() => setEditingStep(null)}>
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <h3 className="font-display text-card-title text-foreground">Stage Properties: {editingStep.name}</h3>
              <button onClick={() => setEditingStep(null)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const nextSteps = currentWf.steps.map((s) => (s.id === editingStep.id ? { ...editingStep } : s));
                saveWorkflowUpdates({ ...currentWf, steps: nextSteps });
                setEditingStep(null);
              }}
              className="flex-1 flex flex-col overflow-hidden text-xs"
            >
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="font-semibold text-foreground block mb-1">Stage Name *</label>
                <input
                  type="text"
                  required
                  value={editingStep.name}
                  onChange={(e) => setEditingStep({ ...editingStep, name: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-semibold text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1">Stage Category</label>
                <select
                  value={editingStep.type}
                  onChange={(e) => setEditingStep({ ...editingStep, type: e.target.value as WorkflowStep['type'] })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="initial">Initial / Logged (New)</option>
                  <option value="in_progress">In Progress / Investigation</option>
                  <option value="pending">Pending / On Hold (Customer/Vendor)</option>
                  <option value="approval">Escalation / Approval Tier</option>
                  <option value="resolved">Resolved / Implemented</option>
                  <option value="closed">Closed / Archived</option>
                </select>
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1">SLA Target Resolution Time (Hours)</label>
                <input
                  type="number"
                  min={1}
                  value={editingStep.slaTimeoutHours || 8}
                  onChange={(e) => setEditingStep({ ...editingStep, slaTimeoutHours: Number(e.target.value) })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1">Description</label>
                <textarea
                  rows={2}
                  value={editingStep.description || ''}
                  onChange={(e) => setEditingStep({ ...editingStep, description: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              </div>

              <div className="border-t border-border p-4 bg-card flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => {
                    handleDeleteStage(editingStep.id, editingStep.name);
                    setEditingStep(null);
                  }}
                  className="rounded-md px-3 py-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-xs font-semibold cursor-pointer"
                >
                  Delete Stage
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingStep(null)}
                    className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer shadow-xs"
                  >
                    Save Properties
                  </button>
                </div>
              </div>
            </form>
        </WorkflowDrawer>
      )}
      {/* MODAL 5: CREATE NEW WORKFLOW DRAWER */}
      {showCreateWorkflowModal && (
        <WorkflowDrawer title="Create new workflow" wide onClose={() => setShowCreateWorkflowModal(false)}>
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <div>
                <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                  <GitBranch className="size-4 text-primary" /> Create New Workflow
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Design an additional lifecycle workflow that can be used for {selectedTicketType} and other ticket types.
                </p>
              </div>
              <button onClick={() => setShowCreateWorkflowModal(false)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveCreateWorkflow} className="flex-1 flex flex-col overflow-hidden text-xs">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-foreground block mb-1">Workflow Name *</label>
                  <input
                    type="text"
                    required
                    value={createWorkflowForm.name}
                    onChange={(e) => setCreateWorkflowForm({ ...createWorkflowForm, name: e.target.value })}
                    placeholder="e.g. Fault Expedited Workflow"
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-semibold text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="font-semibold text-foreground block mb-1">Workflow Code *</label>
                  <input
                    type="text"
                    required
                    value={createWorkflowForm.code}
                    onChange={(e) => setCreateWorkflowForm({ ...createWorkflowForm, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. WF-FLT-02"
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1">Description</label>
                <textarea
                  rows={2}
                  value={createWorkflowForm.description}
                  onChange={(e) => setCreateWorkflowForm({ ...createWorkflowForm, description: e.target.value })}
                  placeholder="Describe when this workflow should be selected..."
                  className="w-full p-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1.5">
                  Applicable Ticket Types (Multi-Select):
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-lg border border-border bg-secondary/30 max-h-48 overflow-y-auto">
                  {ticketTypesList.map((tt) => {
                    const isChecked = createWorkflowForm.allowedTicketTypes.includes(tt.label);
                    return (
                      <label key={tt.id} className="flex items-center gap-2 cursor-pointer text-xs p-1 rounded hover:bg-secondary/60 transition-colors">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...createWorkflowForm.allowedTicketTypes, tt.label]
                              : createWorkflowForm.allowedTicketTypes.filter((t) => t !== tt.label);
                            setCreateWorkflowForm({ ...createWorkflowForm, allowedTicketTypes: next });
                          }}
                          className="rounded border-border text-primary focus:ring-primary/20 shrink-0"
                        />
                        <span className="font-medium text-foreground whitespace-normal break-words">{tt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-4 pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createWorkflowForm.cloneCurrent}
                    onChange={(e) => setCreateWorkflowForm({ ...createWorkflowForm, cloneCurrent: e.target.checked })}
                    className="rounded border-border text-primary focus:ring-primary/20"
                  />
                  <span>Clone stages and action wires from current workflow ({currentWf.name})</span>
                </label>
              </div>

              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateWorkflowModal(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer shadow-xs"
                >
                  Create &amp; Open Workflow
                </button>
              </div>
            </form>
        </WorkflowDrawer>
      )}

      {/* MODAL 6: WORKFLOW SETTINGS & APPLICABLE TICKET TYPES DRAWER */}
      {showWorkflowSettingsModal && (
        <WorkflowDrawer title={`Workflow details: ${currentWf.name}`} wide onClose={() => setShowWorkflowSettingsModal(false)}>
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <div>
                <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                  <Settings className="size-4 text-primary" /> Workflow Configuration: {currentWf.name}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Configure workflow identity and associate with one or more ticket types (e.g. Fault, Incident, Problem).
                </p>
              </div>
              <button onClick={() => setShowWorkflowSettingsModal(false)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveWorkflowSettings} className="flex-1 flex flex-col overflow-hidden text-xs">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-foreground block mb-1">Workflow Name *</label>
                  <input
                    type="text"
                    required
                    value={workflowSettingsForm.name}
                    onChange={(e) => setWorkflowSettingsForm({ ...workflowSettingsForm, name: e.target.value })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-semibold text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="font-semibold text-foreground block mb-1">Workflow Code</label>
                  <input
                    type="text"
                    value={workflowSettingsForm.code}
                    onChange={(e) => setWorkflowSettingsForm({ ...workflowSettingsForm, code: e.target.value.toUpperCase() })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1">Description</label>
                <textarea
                  rows={2}
                  value={workflowSettingsForm.description}
                  onChange={(e) => setWorkflowSettingsForm({ ...workflowSettingsForm, description: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-semibold text-foreground">
                    Applicable Ticket Types (Multi-Select):
                  </label>
                  <span className="text-[11px] text-muted-foreground">
                    Check all ticket types this workflow can be used for
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-lg border border-border bg-secondary/30 max-h-48 overflow-y-auto">
                  {ticketTypesList.map((tt) => {
                    const isChecked = workflowSettingsForm.allowedTicketTypes.includes(tt.label);
                    return (
                      <label key={tt.id} className="flex items-center gap-2 cursor-pointer text-xs p-1 rounded hover:bg-secondary/60 transition-colors">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...workflowSettingsForm.allowedTicketTypes, tt.label]
                              : workflowSettingsForm.allowedTicketTypes.filter((t) => t !== tt.label);
                            setWorkflowSettingsForm({ ...workflowSettingsForm, allowedTicketTypes: next });
                          }}
                          className="rounded border-border text-primary focus:ring-primary/20 shrink-0"
                        />
                        <span className="font-medium text-foreground whitespace-normal break-words">{tt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="wf-is-default-chk"
                  checked={workflowSettingsForm.isDefault}
                  onChange={(e) => setWorkflowSettingsForm({ ...workflowSettingsForm, isDefault: e.target.checked })}
                  className="rounded border-border text-primary focus:ring-primary/20"
                />
                <label htmlFor="wf-is-default-chk" className="cursor-pointer font-medium">
                  Set as default workflow for primary ticket type ({selectedTicketType})
                </label>
              </div>

              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowWorkflowSettingsModal(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer shadow-xs"
                >
                  Save Configuration
                </button>
              </div>
            </form>
        </WorkflowDrawer>
      )}
    </div>
  );
}
